import React, { useState, useEffect } from 'react';
import { Sparkles, Star, Gem, X, Image as ImageIcon, Info, AlertTriangle } from 'lucide-react';
import { apiClient } from '../../utils/apiClient';
import { API_URL } from '../../config';
import PhotoUploadSlot from './PhotoUploadSlot';

// Foto-Slots — Single-Source-of-Truth (Frontend sendet exakt diese Namen).
// MUSS mit /opt/backend/routes/avatar.py übereinstimmen.
// Nur "face_front" ist Pflicht; alle anderen Slots sind optional und steigern die Qualität.
const FACE_SLOTS = [
  { name: 'face_front', label: 'Gesicht vorne',  slotKind: 'face_front', required: true,  example: '/examples/face_front.jpg' },
  { name: 'face_left',  label: 'Gesicht links',  slotKind: 'face_side',  required: false, example: '/examples/face_left.jpg'  },
  { name: 'face_right', label: 'Gesicht rechts', slotKind: 'face_side',  required: false, example: '/examples/face_right.jpg' },
  { name: 'face_back',  label: 'Gesicht hinten', slotKind: 'face_back',  required: false, example: '/examples/face_back.jpg'  },
];
const BODY_SLOTS = [
  { name: 'body_front', label: 'Körper vorne',  slotKind: 'body_front', required: false, example: '/examples/body_front.jpg' },
  { name: 'body_left',  label: 'Körper links',  slotKind: 'body_side',  required: false, example: '/examples/body_left.jpg'  },
  { name: 'body_right', label: 'Körper rechts', slotKind: 'body_side',  required: false, example: '/examples/body_right.jpg' },
  { name: 'body_back',  label: 'Körper hinten', slotKind: 'body_back',  required: false, example: '/examples/body_back.jpg'  },
];

const REQUIRED_SLOT = 'face_front';

const PACKAGES = [
  { id: 'avatar_starter',  name: 'Starter',  icon: Sparkles, price: 9.99,  snaps: 4,  subtext: '1 Avatar + 16 Bilder', isPopular: false },
  { id: 'avatar_standard', name: 'Standard', icon: Star,     price: 24.99, snaps: 15, subtext: '1 Avatar + 60 Bilder', isPopular: true  },
  { id: 'avatar_premium',  name: 'Premium',  icon: Gem,      price: 59.99, snaps: 40, subtext: '1 Avatar + 160 Bilder', isPopular: false },
];

// Beispielbild neben jedem Upload-Slot. Zeigt das echte Bild, sobald es unter
// /examples/<slot>.jpg liegt — bis dahin ein dezenter Platzhalter.
const ExampleImage = ({ src, label }) => {
  const [failed, setFailed] = useState(false);
  return (
    <div className="relative aspect-[3/4] rounded-2xl border-2 border-white/10 bg-white/5 overflow-hidden">
      {!failed ? (
        <img
          src={`${src}?v=2`}
          alt={`Beispiel: ${label}`}
          onError={() => setFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
          <ImageIcon className="w-7 h-7 text-white/25 mb-2" />
          <p className="text-white/40 text-xs leading-tight">Beispielbild folgt</p>
        </div>
      )}
      <div className="absolute top-2 left-2 bg-black/60 text-white/70 text-[10px] font-semibold px-2 py-0.5 rounded-full pointer-events-none">
        Beispiel
      </div>
    </div>
  );
};

// Ein Upload-Slot + zugehöriges Beispielbild nebeneinander.
// Mobil füllt genau ein Paar eine Zeile (grid-cols-2 → Upload | Beispiel).
const SlotPair = ({ slot, value, onChange }) => (
  <div className="grid grid-cols-2 gap-3">
    <PhotoUploadSlot
      slotName={slot.name}
      slotKind={slot.slotKind}
      label={slot.label}
      required={slot.required}
      value={value}
      onChange={onChange}
    />
    <ExampleImage src={slot.example} label={slot.label} />
  </div>
);

const PersonalDataStep = ({ data, updateData, onNext, onBack }) => {
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [userCredits, setUserCredits] = useState(null);
  const [firstAvatarAvailable, setFirstAvatarAvailable] = useState(false);

  const slotState = data.slots || {};
  // Nur das Frontalgesicht ist Pflicht — alles andere ist optional.
  const canProceed = !!(data.name?.trim() && slotState[REQUIRED_SLOT]?.photoId);

  // Credits einmalig laden — entscheidet, ob Package-Modal beim Submit erscheint
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/api/user/credits');
        if (!res.ok || cancelled) return;
        const j = await res.json();
        setUserCredits(j.credits ?? 0);
        setFirstAvatarAvailable(j.first_avatar_available === true);
      } catch (e) {
        console.error('credits load failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const updateSlot = (slotName, value) => {
    const next = { ...slotState };
    if (value === null) delete next[slotName];
    else next[slotName] = value;
    updateData('slots', next);
  };

  const handleSubmit = () => {
    if (!canProceed) return;
    updateData('createError', null); // alten Fehler beim erneuten Versuch verwerfen
    if (firstAvatarAvailable) return onNext();
    if (userCredits === 0) return setShowPackageModal(true);
    onNext();
  };

  const handlePackagePurchase = async (packageId) => {
    try {
      // Wizard-Daten persistieren (Slots inkl. photoIds — beim Stripe-Rückkehr wird wieder hereingeladen)
      const persistable = {
        name: data.name,
        avatarType: data.avatarType,
        slots: Object.fromEntries(
          Object.entries(slotState).map(([k, v]) => [k, { photoId: v.photoId, fileName: v.fileName }]),
        ),
      };
      localStorage.setItem('snapshot_wizard_data', JSON.stringify(persistable));

      const res = await fetch(`${API_URL}/api/stripe/checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin }),
      });
      if (!res.ok) throw new Error(await res.text());
      const j = await res.json();
      if (!j.url) throw new Error('Stripe URL fehlt');
      window.location.href = j.url;
    } catch (e) {
      console.error(e);
      alert('Fehler beim Stripe-Checkout. Bitte erneut versuchen.');
    }
  };

  return (
    <div className="bg-black grain-texture text-white min-h-screen">
      <div className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold mb-3">
              Erstelle deinen <span className="text-[#10B981]">Avatar</span>
            </h1>
            <p className="text-white/70 text-lg">
              Ein paar gute Fotos — und du kannst direkt loslegen.
            </p>
          </div>

          {/* Fehler-Banner — gesetzt, wenn die Avatar-Erstellung fehlschlug.
              Der User landet hier zurück, sieht den Grund und kann es erneut versuchen. */}
          {data.createError && (
            <div className="mb-8 p-4 rounded-2xl bg-red-500/10 border-2 border-red-500/40 text-red-300 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-red-200">Avatar-Erstellung fehlgeschlagen</p>
                <p className="text-sm opacity-90 mt-0.5">{data.createError}</p>
                <p className="text-sm opacity-70 mt-1">
                  Bitte passe deine Fotos an und versuche es erneut — deine Eingaben bleiben erhalten.
                </p>
              </div>
            </div>
          )}

          {/* Card — Name + Fotos */}
          <div className="bg-black border-2 border-[#10B981]/20 rounded-2xl p-6 sm:p-8 mb-8 glow-green-soft relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#10B981]/5 blur-3xl rounded-full pointer-events-none"></div>

            {/* Name */}
            <div className="mb-8 relative z-10">
              <label className="block text-base font-bold text-[#10B981] mb-3">
                Wie soll dein Avatar heißen?
              </label>
              <input
                type="text"
                value={data.name || ''}
                onChange={(e) => updateData('name', e.target.value)}
                placeholder="z. B. Anna"
                maxLength={60}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border-2 border-white/20 text-white placeholder-white/30 focus:border-[#10B981] focus:outline-none transition-all"
              />
            </div>

            {/* Fotos */}
            <div className="relative z-10">
              <h2 className="text-2xl font-bold text-[#10B981] mb-3">Lade deine Fotos hoch</h2>
              <p className="text-white/60 text-sm mb-2">
                Jedes Foto wird sofort geprüft. Schlechte Fotos werden mit einem roten Rahmen markiert —
                lade dann einfach ein besseres Foto in den gleichen Slot.
              </p>
              <p className="text-white/85 text-sm font-medium mb-8">
                Je mehr Fotos du hochlädst, desto genauer wird dein Avatar.
              </p>

              {/* Gesicht */}
              <div className="mb-10">
                <h3 className="text-lg font-bold text-[#10B981] mb-1">Dein Gesicht</h3>
                <p className="text-white/50 text-sm mb-5">
                  Die Frontansicht ist <span className="text-[#10B981] font-semibold">Pflicht</span>.
                  Steigere die Qualität, indem du weitere Bilder hinzufügst.
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {FACE_SLOTS.map((s) => (
                    <SlotPair key={s.name} slot={s} value={slotState[s.name]} onChange={updateSlot} />
                  ))}
                </div>
              </div>

              {/* Körper */}
              <div>
                <h3 className="text-lg font-bold text-[#10B981] mb-1">Dein Körper</h3>
                <p className="text-white/50 text-sm mb-3">
                  Optional — ein Ganzkörperbild von vorne reicht schon.
                  Verbessere die Qualität, indem du weitere Fotos hinzufügst.
                </p>
                <div className="mb-5 p-3 rounded-xl bg-[#10B981]/5 border border-[#10B981]/30 text-white/70 text-xs flex items-start gap-2">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#10B981]" />
                  <span>
                    Wenn du kein Körperbild hochlädst, wird ein Körper erfunden — oder so dargestellt,
                    wie du ihn in deinen Anweisungen beschreibst.
                  </span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {BODY_SLOTS.map((s) => (
                    <SlotPair key={s.name} slot={s} value={slotState[s.name]} onChange={updateSlot} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Aktions-Leiste */}
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-between">
            <button
              onClick={onBack}
              className="px-6 py-3 rounded-xl font-semibold text-white/70 border-2 border-white/20 hover:bg-white/5 hover:text-white transition-all"
            >
              Zurück
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canProceed}
              className={`px-8 py-3 rounded-xl font-bold transition-all ${
                canProceed
                  ? 'bg-gradient-to-br from-[#10B981] to-[#059669] text-black glow-green pulse-glow shine-effect'
                  : 'bg-white/10 text-white/40 cursor-not-allowed border-2 border-white/20'
              }`}
            >
              Avatar erstellen →
            </button>
          </div>
        </div>
      </div>

      {/* Package-Modal — fällt nur an, wenn 0 Credits + kein Gratis-Avatar */}
      {showPackageModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-black border-2 border-[#10B981]/30 rounded-3xl max-w-3xl w-full p-6 sm:p-8 my-auto relative">
            <button
              onClick={() => setShowPackageModal(false)}
              className="absolute top-4 right-4 text-white/60 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#10B981] mb-2 text-center">
              Wähle dein Avatar-Paket
            </h2>
            <p className="text-white/60 text-sm mb-8 text-center">
              Du brauchst SNAPs, um diesen Avatar zu erstellen.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {PACKAGES.map((pkg) => {
                const Icon = pkg.icon;
                return (
                  <div
                    key={pkg.id}
                    className={`relative rounded-2xl p-5 border-2 transition-all cursor-pointer hover:scale-105 ${
                      pkg.isPopular ? 'border-[#10B981] glow-green-soft' : 'border-white/20 hover:border-[#10B981]/60'
                    }`}
                    onClick={() => handlePackagePurchase(pkg.id)}
                  >
                    {pkg.isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#10B981] text-black text-xs font-bold px-3 py-1 rounded-full">
                        Beliebt
                      </div>
                    )}
                    <Icon className="w-8 h-8 text-[#10B981] mb-3" />
                    <h3 className="text-xl font-bold text-white">{pkg.name}</h3>
                    <p className="text-3xl font-bold text-[#10B981] my-2">€{pkg.price}</p>
                    <p className="text-white/60 text-sm">{pkg.subtext}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalDataStep;
